<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SearchBox from '@/components/SearchBox.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useAccountsEditor } from '@/composables/useAccountsEditor'
import { ACCOUNT_SEARCH_MIN, searchAccounts } from '@/settings/accountSearch'
import { accountsPath, NEW_ACCOUNT_PATH } from '@/settings/accountsNav'

// The first level of the accounts settings: a row per account that opens it, a search from the sixth account on (`?q=`, replaced,
// so it makes no history entries) and the add row at the end.
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const editor = useAccountsEditor()

const query = ref(typeof route.query.q === 'string' ? route.query.q : '')
const searchable = computed(() => editor.accounts.value.length >= ACCOUNT_SEARCH_MIN)
const shown = computed(() => (searchable.value ? searchAccounts(editor.accounts.value, query.value) : editor.accounts.value))

watch(query, (q) => {
  const rest = { ...route.query }
  delete rest.q
  void router.replace({ query: q ? { ...rest, q } : rest })
})
</script>

<template>
  <div class="account-list">
    <UiGroup v-if="searchable">
      <SearchBox v-model="query" :placeholder="t('list.search')" :clear-label="t('list.clearSearch')" />
      <template #footer>
        <p v-if="query && !shown.length">{{ t('accounts.noMatches') }}</p>
      </template>
    </UiGroup>

    <UiGroup v-if="shown.length">
      <UiList>
        <UiRow v-for="account in shown" :key="account.id" :to="accountsPath(account.id)" class="entry" :data-account-id="account.id">
          <span class="text">
            <span class="name">
              {{ account.name }}
              <span v-if="account.readonly" class="badge">{{ t('accounts.readonlyBadge') }}</span>
            </span>
            <span class="ui-hint url">{{ account.url }}</span>
          </span>
          <span class="go" aria-hidden="true">›</span>
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <template #footer>
        <p v-if="!editor.accounts.value.length">{{ t('accounts.none') }}</p>
      </template>
      <UiList>
        <UiRow :to="NEW_ACCOUNT_PATH" class="entry add" data-testid="add-account">＋ {{ t('accounts.addTitle') }}</UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.add {
  color: var(--tg-link-color);
}

.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.name,
.url {
  overflow-wrap: anywhere;
}

.badge {
  margin-left: 0.4rem;
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.5rem;
  background: var(--tg-secondary-bg-color);
  color: var(--tg-hint-color);
}

.go {
  flex: none;
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}
</style>
